import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router'

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'subtle'
export type ButtonSize = 'sm' | 'md' | 'lg'

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-ink-900 text-paper shadow-ink hover:bg-ink-800 disabled:bg-ink-300 disabled:text-ink-50 disabled:shadow-none',
  secondary:
    'bg-surface text-ink-900 shadow-[0_0_0_1px_oklch(0.165_0.012_76/0.1),0_1px_2px_oklch(0.165_0.012_76/0.05),inset_0_1px_0_white] hover:bg-ink-50 disabled:text-ink-400 disabled:hover:bg-surface',
  danger:
    'bg-danger-600 text-white shadow-[inset_0_1px_0_oklch(1_0_0/0.15),0_8px_20px_-10px_oklch(0.54_0.19_25/0.7)] hover:bg-danger-700 disabled:bg-danger-100 disabled:text-danger-500 disabled:shadow-none',
  ghost: 'text-ink-700 hover:bg-ink-950/[0.05] hover:text-ink-900 disabled:text-ink-300 disabled:hover:bg-transparent',
  subtle: 'bg-ink-950/[0.05] text-ink-800 hover:bg-ink-950/[0.08] disabled:text-ink-300 disabled:hover:bg-ink-950/[0.05]',
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[13px] gap-1.5 rounded-[9px]',
  md: 'h-9.5 px-3.5 text-sm gap-2 rounded-[10px]',
  lg: 'h-11 px-5 text-[15px] gap-2 rounded-xl',
}

function buttonClasses({ variant = 'primary', size = 'md', className = '' }: { variant?: ButtonVariant; size?: ButtonSize; className?: string }) {
  return `inline-flex shrink-0 items-center justify-center whitespace-nowrap font-medium tracking-[-0.005em] transition-[background-color,color,box-shadow,transform] duration-200 ease-spring active:scale-[0.98] disabled:cursor-not-allowed disabled:active:scale-100 ${variantClasses[variant]} ${sizeClasses[size]} ${className}`
}

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: ReactNode
  trailingIcon?: ReactNode
}

export function Button({ variant = 'primary', size = 'md', className = '', icon, trailingIcon, children, type = 'button', ...props }: Props) {
  return (
    <button type={type} className={buttonClasses({ variant, size, className })} {...props}>
      {icon ? <span className="-ml-0.5 inline-flex shrink-0 opacity-80">{icon}</span> : null}
      {children}
      {trailingIcon ? <span className="-mr-0.5 inline-flex shrink-0 opacity-80">{trailingIcon}</span> : null}
    </button>
  )
}

/** A router Link with button styling — avoids nesting a <button> inside an <a>. */
export function ButtonLink({
  variant = 'primary',
  size = 'md',
  className = '',
  icon,
  trailingIcon,
  children,
  ...props
}: LinkProps & { variant?: ButtonVariant; size?: ButtonSize; icon?: ReactNode; trailingIcon?: ReactNode }) {
  return (
    <Link className={buttonClasses({ variant, size, className })} {...props}>
      {icon ? <span className="-ml-0.5 inline-flex shrink-0 opacity-80">{icon}</span> : null}
      {children}
      {trailingIcon ? <span className="-mr-0.5 inline-flex shrink-0 opacity-80">{trailingIcon}</span> : null}
    </Link>
  )
}

/** Square icon-only button for toolbars and table rows. */
export function IconButton({
  label,
  className = '',
  variant = 'ghost',
  size = 'md',
  children,
  type = 'button',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; variant?: ButtonVariant; size?: 'sm' | 'md' }) {
  const dims = size === 'sm' ? 'size-8 rounded-[9px]' : 'size-9.5 rounded-[10px]'
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={`inline-flex shrink-0 items-center justify-center transition-[background-color,color,transform] duration-200 ease-spring active:scale-[0.96] disabled:cursor-not-allowed ${variantClasses[variant]} ${dims} ${className}`}
      {...props}
    >
      {children}
    </button>
  )
}
